<template>
  <v-container fluid>
    <template v-if="maintain">
      <v-row justify="center" align="center" style="min-height: 400px;">
        <v-col cols="12" class="text-center">
          <v-icon size="80" color="warning" class="mb-4">mdi-wrench</v-icon>
          <h2 class="text-h4 font-weight-bold warning--text">{{ pageMessage }}</h2>
        </v-col>
      </v-row>
    </template>
    <template v-else>
      <template v-if="CheckAdmin || CheckSubAdmin">
        <!-- ปุ่ม การทำรายการ โครงการ, เดินทางไปปฏิบัติงาน -->
        <v-row class="mb-4">
          <v-col cols="12" md="6" class="d-flex justify-start">
            <v-btn-toggle v-model="isProject" mandatory color="primary" group @change="onReportTypeChange">
              <v-btn :value="true" class="text-none">
                <v-icon left>mdi-chart-bar</v-icon>
                <span class="font-weight-bold">รายงานโครงการ</span>
              </v-btn>
              <v-btn :value="false" class="text-none">
                <v-icon left>mdi-chart-line</v-icon>
                <span class="font-weight-bold">รายงานเดินทางไปปฏิบัติงาน</span>
              </v-btn>
            </v-btn-toggle>
          </v-col>
        </v-row>
        <template v-if="isProject">
          <!-- Chart ที่ 1: สรุปโครงการจัดแล้ว/ยังไม่จัด -->
          <v-card class="mx-auto my-8" elevation="12" max-width="1200" style="border-radius: 16px;height: 550px;">
            <v-card-title class="justify-center text-h5 font-weight-bold primary--text py-6">
              <v-icon left color="primary" size="28">mdi-chart-pie</v-icon>
              <span class="ml-2">สรุปสถานะโครงการ</span>
            </v-card-title>

            <!-- Fiscal Year Selector -->
            <v-card-subtitle class="text-center pb-2">
              <v-row justify="center" class="mb-4">
                <v-col cols="auto">
                  <v-select v-model="selectedFiscalYear" :items="FiscalYearDataSel" item-text="yearth" item-value="year"
                    label="ปีงบประมาณ" outlined dense style="min-width: 200px;" prepend-inner-icon="mdi-calendar"
                    @change="onFiscalYearChange" hide-details class="fiscal-year-select">
                    <template v-slot:selection="{ item }">
                      <span class="font-weight-medium primary--text">{{ item.yearth }}</span>
                    </template>
                    <template v-slot:item="{ item }">
                      <span class="font-weight-medium">{{ item.yearth }}</span>
                    </template>
                  </v-select>
                </v-col>
              </v-row>
            </v-card-subtitle>

            <!-- Chart Type Selector for Chart 1 -->
            <v-card-subtitle class="text-center pb-2 pt-0">
              <v-chip-group v-model="statusChartType" mandatory class="justify-center">
                <v-chip v-for="type in pieCompatibleChartTypes" :key="type.type" :value="type.type" small
                  color="primary" outlined class="mx-2 my-1" style="transition: all 0.3s ease;">
                  <v-icon left size="16">{{ type.icon }}</v-icon>
                  <span class="font-weight-medium">{{ type.label }}</span>
                </v-chip>
              </v-chip-group>
            </v-card-subtitle>

            <v-card-text class="text-center px-6 pb-8">
              <div class="chart-container mb-4">
                <component :is="getChartComponent(statusChartType)"
                  :chart-data="getProjectStatusChartData(statusChartType)"
                  :chart-options="getChartOptions(statusChartType, 'status')">
                </component>
              </div>
            </v-card-text>
          </v-card>

          <!-- Chart ที่ 2: งบประมาณอนุมัติ vs เบิกจ่ายจริง -->
          <v-card class="mx-auto my-8" elevation="12" max-width="1200" style="border-radius: 16px;height: 500px">
            <v-card-title class="justify-center text-h5 font-weight-bold primary--text py-6">
              <v-icon left color="primary" size="28">mdi-chart-bar</v-icon>
              <span class="ml-2">งบประมาณอนุมัติ vs เบิกจ่ายจริง</span>
            </v-card-title>

            <!-- Chart Type Selector for Chart 2 -->
            <v-card-subtitle class="text-center pb-2">
              <v-chip-group v-model="budgetChartType" mandatory class="justify-center">
                <v-chip v-for="type in chartTypes" :key="type.type" :value="type.type" small color="success" outlined
                  class="mx-2 my-1" style="transition: all 0.3s ease;">
                  <v-icon left size="16">{{ type.icon }}</v-icon>
                  <span class="font-weight-medium">{{ type.label }}</span>
                </v-chip>
              </v-chip-group>
            </v-card-subtitle>

            <v-card-text class="text-center px-6 pb-8">
              <div class="chart-container mb-4">
                <component :is="getChartComponent(budgetChartType)" :chart-data="getBudgetChartData(budgetChartType)"
                  :chart-options="getChartOptions(budgetChartType, 'budget')" style="height: 350px;">
                </component>
              </div>
            </v-card-text>
          </v-card>

          <!-- Chart ที่ 3: สรุปโครงการตามประเภท -->
          <v-card class="mx-auto my-8" elevation="12" max-width="1200" style="border-radius: 16px; height: 780px;">
            <v-card-title class="justify-center text-h5 font-weight-bold success--text py-6">
              <v-icon left color="success" size="28">mdi-chart-donut-variant</v-icon>
              <span class="ml-2">สรุปโครงการตามประเภท</span>
            </v-card-title>

            <!-- Chart Type Selector for Category Chart -->
            <v-card-subtitle class="text-center pb-2">
              <v-chip-group v-model="categoryChartType" mandatory class="justify-center">
                <v-chip v-for="type in chartTypes" :key="type.type" :value="type.type" small color="info" outlined
                  class="mx-2 my-1" style="transition: all 0.3s ease;">
                  <v-icon left size="16">{{ type.icon }}</v-icon>
                  <span class="font-weight-medium">{{ type.label }}</span>
                </v-chip>
              </v-chip-group>
            </v-card-subtitle>

            <v-card-text class="text-center px-6 pb-8">
              <div class="chart-container mb-4">
                <component :is="getChartComponent(categoryChartType)"
                  :chart-data="getCategoryChartData(categoryChartType)"
                  :chart-options="getChartOptions(categoryChartType, 'category')" style="height: 300px;">
                </component>

                <!-- Summary Cards for Category Projects -->
                <div class="category-summary-container mt-6" v-if="getCategorySummary().length > 0">
                  <v-row>
                    <v-col v-for="(summary, index) in getCategorySummary()" :key="index" cols="12" sm="6" lg="3"
                      class="mb-3">
                      <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                          <v-card elevation="4" class="summary-card pa-4 h-100"
                            :style="`background: linear-gradient(135deg, ${getCategoryColor(index)}15 0%, ${getCategoryColor(index)}05 100%); border-left: 6px solid ${getCategoryColor(index)}; transition: all 0.3s ease; cursor: default;`"
                            hover v-bind="attrs" v-on="on">
                            <div class="d-flex flex-column h-100">
                              <!-- Header Section -->
                              <div class="d-flex align-center mb-3">
                                <v-avatar :color="getCategoryColor(index)" size="48" class="mr-3 elevation-2"
                                  style="border: 2px solid white;">
                                  <v-icon color="white" size="24">{{ getCategoryIcon(index) }}</v-icon>
                                </v-avatar>
                                <div class="flex-grow-1">
                                  <div class="text-subtitle-2 font-weight-bold primary--text mb-1">{{ summary.name }}
                                  </div>
                                  <div class="text-caption grey--text text--darken-1"><span
                                      class="font-weight-bold">ประเภทโครงการ</span></div>
                                </div>
                              </div>

                              <!-- Main Value -->
                              <div class="text-center mb-3">
                                <div class="text-h4 font-weight-bold mb-1 animated-number"
                                  :style="`color: ${getCategoryColor(index)}`">
                                  {{ summary.count || 0 }}
                                </div>
                                <div class="text-subtitle-2 grey--text text--darken-2 font-weight-medium">
                                  <span class="font-weight-bold">โครงการทั้งหมด</span>
                                </div>
                              </div>

                              <!-- Financial Information -->
                              <div class="mt-auto">
                                <v-divider class="mb-3"></v-divider>
                                <div class="d-flex justify-space-between align-center mb-1">
                                  <span
                                    class="text-caption font-weight-medium grey--text text--darken-2">งบอนุมัติ:</span>
                                  <span class="text-caption font-weight-bold success--text">{{
                                    formatFullCurrency(summary.approvedBudget || 0) }}</span>
                                </div>
                                <div class="d-flex justify-space-between align-center">
                                  <span
                                    class="text-caption font-weight-medium grey--text text--darken-2">เบิกจ่ายแล้ว:</span>
                                  <span class="text-caption font-weight-bold warning--text">{{
                                    formatFullCurrency(summary.disbursedBudget || 0) }}</span>
                                </div>

                                <!-- Progress Bar -->
                                <div class="mt-2">
                                  <v-progress-linear
                                    :value="(summary.approvedBudget || 0) > 0 ? ((summary.disbursedBudget || 0) / (summary.approvedBudget || 1)) * 100 : 0"
                                    height="6" :color="getCategoryColor(index)" background-color="grey lighten-3"
                                    rounded class="animated-progress"></v-progress-linear>
                                  <div class="text-caption grey--text text--darken-1 text-center mt-1">
                                    {{ (summary.approvedBudget || 0) > 0 ? Math.round(((summary.disbursedBudget || 0) /
                                      (summary.approvedBudget || 1)) * 100) : 0 }}% เบิกจ่าย
                                  </div>
                                </div>
                              </div>
                            </div>
                          </v-card>
                        </template>
                        <span>
                          {{ summary.name }}<br>
                          จำนวนโครงการ: {{ summary.count || 0 }} โครงการ<br>
                          งบประมาณอนุมัติ: {{ formatFullCurrency(summary.approvedBudget || 0) }}<br>
                          เบิกจ่ายแล้ว: {{ formatFullCurrency(summary.disbursedBudget || 0) }}<br>
                          คงเหลือ: {{ formatFullCurrency((summary.approvedBudget || 0) - (summary.disbursedBudget || 0))
                          }}
                        </span>
                      </v-tooltip>
                    </v-col>
                  </v-row>
                </div>
                <!-- No Data Message for Category Summary -->
                <div v-else class="text-center py-8">
                  <v-icon size="60" color="grey lighten-2" class="mb-4">mdi-chart-pie-outline</v-icon>
                  <p class="grey--text text-h6 font-weight-medium">ไม่มีข้อมูลสรุปโครงการ</p>
                  <p class="grey--text text-caption">เลือกปีงบประมาณที่มีข้อมูลโครงการ</p>
                </div>
              </div>

            </v-card-text>
          </v-card>

          <!-- Chart ที่ 4: งบประมาณตามประเภทโครงการ -->
          <v-card class="mx-auto my-8" elevation="12" max-width="1200" style="border-radius: 16px;height: 780px;">
            <v-card-title class="justify-center text-h5 font-weight-bold warning--text py-6">
              <v-icon left color="warning" size="28">mdi-chart-multiline</v-icon>
              <span class="ml-2">งบประมาณตามประเภทโครงการ</span>
            </v-card-title>

            <!-- Chart Type Selector for Category Budget Chart -->
            <v-card-subtitle class="text-center pb-2">
              <v-chip-group v-model="categoryBudgetChartType" mandatory class="justify-center">
                <v-chip v-for="type in chartTypes" :key="type.type" :value="type.type" small color="orange" outlined
                  class="mx-2 my-1" style="transition: all 0.3s ease;">
                  <v-icon left size="16">{{ type.icon }}</v-icon>
                  <span class="font-weight-medium">{{ type.label }}</span>
                </v-chip>
              </v-chip-group>
            </v-card-subtitle>

            <v-card-text class="text-center px-6 pb-8">
              <div class="chart-container mb-4">
                <component :is="getChartComponent(categoryBudgetChartType)"
                  :chart-data="getCategoryBudgetChartData(categoryBudgetChartType)"
                  :chart-options="getChartOptions(categoryBudgetChartType, 'categoryBudget')" style="height: 300px;">
                </component>

                <!-- Summary Cards for Category Budget -->
                <div class="category-summary-container mt-6" v-if="getCategoryBudgetSummary().length > 0">
                  <v-row>
                    <v-col v-for="(summary, index) in getCategoryBudgetSummary()" :key="index" cols="12" sm="6" lg="3"
                      class="mb-3">
                      <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                          <v-card elevation="4" class="summary-card pa-4 h-100"
                            :style="`background: linear-gradient(135deg, ${getCategoryColor(index)}15 0%, ${getCategoryColor(index)}05 100%); border-left: 6px solid ${getCategoryColor(index)}; transition: all 0.3s ease; cursor: default;`"
                            hover v-bind="attrs" v-on="on">
                            <div class="d-flex flex-column h-100">
                              <!-- Header Section -->
                              <div class="d-flex align-center mb-3">
                                <v-avatar :color="getCategoryColor(index)" size="48" class="mr-3 elevation-2"
                                  style="border: 2px solid white;">
                                  <v-icon color="white" size="24">{{ getCategoryIcon(index) }}</v-icon>
                                </v-avatar>
                                <div class="flex-grow-1">
                                  <div class="text-subtitle-2 font-weight-bold primary--text mb-1">{{ summary.name }}
                                  </div>
                                  <div class="text-caption grey--text text--darken-1"><span
                                      class="font-weight-bold">เบิกจ่ายแล้ว</span></div>
                                </div>
                              </div>

                              <!-- Main Value -->
                              <div class="text-center mb-3">
                                <h3 class="font-weight-bold mb-1 animated-number"
                                  :style="`color: ${getCategoryColor(index)}`">
                                  {{ formatFullCurrency(summary.totalBudget || 0) }}
                                </h3>
                                <div class="text-caption grey--text text--darken-2 font-weight-medium">
                                  <span class="font-weight-bold">รายละเอียด</span>
                                </div>
                              </div>

                              <!-- Financial Information -->
                              <div class="mt-auto">
                                <v-divider class="mb-3"></v-divider>
                                <div class="d-flex justify-space-between align-center mb-1">
                                  <span class="font-weight-medium grey--text text--darken-2">งบอนุมัติ:</span>
                                  <span class="font-weight-bold success--text">{{
                                    formatFullCurrency(summary.approvedBudget
                                      || 0) }}</span>
                                </div>
                                <div class="d-flex justify-space-between align-center">
                                  <span class="font-weight-medium grey--text text--darken-2">เบิกจ่ายแล้ว:</span>
                                  <span class="font-weight-bold warning--text">{{
                                    formatFullCurrency(summary.disbursedBudget
                                      || 0) }}</span>
                                </div>

                                <!-- Progress Bar -->
                                <div class="mt-2">
                                  <v-progress-linear
                                    :value="(summary.approvedBudget || 0) > 0 ? ((summary.disbursedBudget || 0) / (summary.approvedBudget || 1)) * 100 : 0"
                                    height="6" :color="getCategoryColor(index)" background-color="grey lighten-3"
                                    rounded class="animated-progress"></v-progress-linear>
                                  <div class="text-caption grey--text text--darken-1 text-center mt-1">
                                    <span class="font-weight-bold">{{ (summary.approvedBudget || 0) > 0 ?
                                      Math.round(((summary.disbursedBudget || 0) / (summary.approvedBudget || 1)) * 100)
                                      :
                                      0
                                      }}% เบิกจ่าย</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </v-card>
                        </template>
                        <span>
                          {{ summary.name }}<br>
                          <!-- งบประมาณรวม: {{ formatFullCurrency(summary.totalBudget || 0) }}<br> -->
                          <span class="font-weight-bold">งบประมาณอนุมัติ: {{ formatFullCurrency(summary.approvedBudget
                            ||
                            0)
                            }}</span><br>
                          <span class="font-weight-bold">เบิกจ่ายแล้ว: {{ formatFullCurrency(summary.disbursedBudget ||
                            0)
                            }}</span><br>
                          <span class="font-weight-bold">คงเหลือ: {{ formatFullCurrency((summary.approvedBudget || 0) -
                            (summary.disbursedBudget || 0)) }}</span>
                        </span>
                      </v-tooltip>
                    </v-col>
                  </v-row>
                </div>
                <!-- No Data Message for Category Budget Summary -->
                <div v-else class="text-center py-8">
                  <v-icon size="60" color="grey lighten-2" class="mb-4">mdi-currency-usd-outline</v-icon>
                  <p class="grey--text text-h6 font-weight-medium">ไม่มีข้อมูลสรุปงบประมาณ</p>
                  <p class="grey--text text-caption">เลือกปีงบประมาณที่มีข้อมูลโครงการ</p>
                </div>
              </div>

            </v-card-text>
          </v-card>

          <!-- ปุ่มเปิด Dialog Chart ที่ 5 -->
          <v-card class="mx-auto my-8" elevation="12" max-width="1200" style="border-radius: 16px;">
            <v-card-text class="text-center py-8">
              <v-btn color="success" x-large elevation="4" style="border-radius: 12px; padding: 16px 32px;"
                @click="openDepartmentReportDialog">
                <v-icon left size="24">mdi-domain</v-icon>
                <span class="font-weight-bold text-h6">รายงานตามหน่วยงาน</span>
              </v-btn>
            </v-card-text>
          </v-card>

          <!-- Dialog รายงานตามหน่วยงาน -->
          <v-dialog v-model="dialogDepartmentReport" max-width="1400" scrollable>
            <v-card style="border-radius: 16px;">
              <v-card-title class="text-h4 primary white--text py-6" style="border-radius: 16px 16px 0 0;">
                <v-icon left color="white" size="32">mdi-domain</v-icon>
                <span class="ml-3 font-weight-bold">รายงานสรุปโครงการตามหน่วยงาน</span>
              </v-card-title>

              <!-- Search Bar -->
              <v-card-subtitle class="px-6 pt-4 pb-2">
                <v-text-field v-model="searchDepartment" prepend-inner-icon="mdi-magnify" label="ค้นหาหน่วยงาน..."
                  outlined dense clearable @click:clear="clearDepartmentSearch" hide-details style="max-width: 400px;"
                  class="mx-auto mt-4"></v-text-field>
              </v-card-subtitle>

              <v-card-text style="max-height: 1000px; padding: 24px;">
                <v-row>
                  <v-col v-for="group in filteredDepartmentGroups" :key="group.dep_group_id" cols="12" lg="6" xl="4">
                    <v-card elevation="8" class="mb-6 mt-6" style="border-radius: 12px; height: 100%;">
                      <v-card-title class="text-h6 secondary--text d-flex align-center py-4"
                        style="background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%); border-radius: 12px 12px 0 0;">
                        <v-icon left color="secondary" size="20">mdi-domain</v-icon>
                        <span class="font-weight-bold ml-2">{{ group.dep_group_name }}</span>
                      </v-card-title>
                      <v-card-text v-if="group.hasData" class="pa-4">
                        <!-- Chart Type Selector for Department Status Chart -->
                        <div class="mb-4">
                          <v-subheader class="px-0 font-weight-bold">สถานะโครงการ</v-subheader>
                          <v-chip-group v-model="group.statusChartType" mandatory class="justify-center mb-3">
                            <v-chip v-for="type in pieCompatibleChartTypes" :key="type.type" :value="type.type" x-small
                              color="info" outlined class="mx-1">
                              <v-icon left size="10">{{ type.icon }}</v-icon>
                              <span style="font-size: 10px;">{{ type.shortLabel || type.label }}</span>
                            </v-chip>
                          </v-chip-group>

                          <!-- Chart สถานะโครงการตามหน่วยงาน -->
                          <div class="chart-container-small mb-4"
                            style="border: 1px solid #e0e0e0; border-radius: 8px; padding: 6px;">
                            <component class="chart-container-small"
                              :is="getChartComponent(group.statusChartType || 'pie')"
                              :chart-data="group.statusChartData"
                              :chart-options="getChartOptions(group.statusChartType || 'pie', 'miniStatus')">
                            </component>
                          </div>
                        </div>

                        <!-- Chart Type Selector for Department Budget Chart -->
                        <div>
                          <v-subheader class="px-0 font-weight-bold">งบประมาณ</v-subheader>
                          <v-chip-group v-model="group.budgetChartType" mandatory class="justify-center mb-3">
                            <v-chip v-for="type in chartTypes" :key="type.type" :value="type.type" x-small
                              color="warning" outlined class="mx-1">
                              <v-icon left size="10">{{ type.icon }}</v-icon>
                              <span style="font-size: 10px;">{{ type.shortLabel || type.label }}</span>
                            </v-chip>
                          </v-chip-group>

                          <!-- Chart งบประมาณตามหน่วยงาน -->
                          <div class="chart-container-small"
                            style="border: 1px solid #e0e0e0; border-radius: 8px; padding: 6px;">
                            <component class="chart-container-small"
                              :is="getChartComponent(group.budgetChartType || 'bar')"
                              :chart-data="group.budgetChartData"
                              :chart-options="getChartOptions(group.budgetChartType || 'bar', 'miniBudget')">
                            </component>
                          </div>
                        </div>
                      </v-card-text>
                      <v-card-text v-else class="text-center py-8">
                        <v-icon size="80" color="grey lighten-2" class="mb-4">mdi-inbox-outline</v-icon>
                        <h1 class="grey--text font-weight-medium">ไม่มีข้อมูลโครงการ</h1>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>

                <!-- No Results Message -->
                <v-row v-if="filteredDepartmentGroups.length === 0" justify="center" class="mt-8">
                  <v-col cols="12" class="text-center">
                    <v-icon size="80" color="grey lighten-2" class="mb-4">mdi-file-search-outline</v-icon>
                    <h3 class="grey--text text-h5 font-weight-medium">ไม่พบหน่วยงานที่ค้นหา</h3>
                    <p class="grey--text">ลองใช้คำค้นหาอื่น หรือล้างช่องค้นหาเพื่อดูทั้งหมด</p>
                  </v-col>
                </v-row>
              </v-card-text>
              <v-card-actions class="py-4 px-6">
                <v-spacer></v-spacer>
                <v-btn color="primary" large elevation="2" style="border-radius: 8px; padding: 12px 24px;"
                  @click="dialogDepartmentReport = false">
                  <v-icon left>mdi-close</v-icon>
                  <span class="font-weight-bold">ปิด</span>
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </template>
        <template v-else>
          <div id="travelReport">
            <!-- Fiscal Year Selector -->
            <v-row justify="center" class="mb-4">
              <v-col cols="auto">
                <v-select v-model="selectedFiscalYear" :items="FiscalYearDataSel" item-text="yearth" item-value="year"
                  label="ปีงบประมาณ" outlined dense style="min-width: 200px;" prepend-inner-icon="mdi-calendar"
                  @change="onFiscalYearChange" hide-details class="fiscal-year-select">
                  <template v-slot:selection="{ item }">
                    <span class="font-weight-medium primary--text">{{ item.yearth }}</span>
                  </template>
                  <template v-slot:item="{ item }">
                    <span class="font-weight-medium">{{ item.yearth }}</span>
                  </template>
                </v-select>
              </v-col>
            </v-row>
            <v-row justify="center" class="mb-4">
                <v-col cols="12" md="6">
                    <v-text-field
                    v-model="searchTravel"
                    prepend-inner-icon="mdi-magnify"
                    label="ค้นหาหน่วยงาน..."
                    outlined
                    dense
                    clearable
                    hide-details
                    ></v-text-field>
                </v-col>
            </v-row>

            <v-row class="my-4 justify-center">
              <v-col cols="12" md="6">
                <v-card elevation="12" class="pa-6 text-center" style="border-radius: 16px; background: linear-gradient(135deg, #FFF3E0 0%, #FFE0B2 100%);">
                  <v-icon size="64" color="orange darken-3" class="mb-4">mdi-cash-check</v-icon>
                  <div class="text-h4 font-weight-bold orange--text text--darken-4">{{ formatFullCurrency(travelBudgetSummary.totalDisbursed) }}</div>
                  <div class="text-subtitle-1 font-weight-medium grey--text text--darken-2 mt-2">งบประมาณที่เบิกจ่ายแล้ว</div>
                </v-card>
              </v-col>
              <!-- <v-col cols="12" md="6">
                <v-card elevation="12" class="pa-6 text-center" style="border-radius: 16px; background: linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%);">
                  <v-icon size="64" color="green darken-3" class="mb-4">mdi-cash-multiple</v-icon>
                  <div class="text-h4 font-weight-bold green--text text--darken-4">{{ formatFullCurrency(travelBudgetSummary.remaining) }}</div>
                  <div class="text-subtitle-1 font-weight-medium grey--text text--darken-2 mt-2">งบประมาณคงเหลือ</div>
                </v-card>
              </v-col> -->
            </v-row>

            <v-row>
              <v-col cols="12">
                <v-card class="mx-auto my-4" elevation="12" style="border-radius: 16px;">
                  <v-card-title class="justify-center text-h5 font-weight-bold primary--text py-4">
                    <v-icon left color="primary" size="28">mdi-podium-gold</v-icon>
                    <span class="ml-2">10 อันดับหน่วยงานที่เบิกจ่ายเยอะที่สุด</span>
                  </v-card-title>
                  <v-card-subtitle class="text-center pb-2 pt-0">
                    <v-chip-group v-model="top10ChartType" mandatory class="justify-center">
                      <v-chip v-for="type in chartTypes" :key="type.type" :value="type.type" small
                        color="primary" outlined class="mx-2 my-1" style="transition: all 0.3s ease;">
                        <v-icon left size="16">{{ type.icon }}</v-icon>
                        <span class="font-weight-medium">{{ type.label }}</span>
                      </v-chip>
                    </v-chip-group>
                  </v-card-subtitle>
                  <v-card-text class="text-center px-6 pb-8">
                    <div class="chart-container" style="height: 500px;">
                      <component :is="getChartComponent(top10ChartType)" :chart-data="top10DisbursedChartData" :chart-options="getChartOptions(top10ChartType, 'top10')" style="height: 100%; width: 100%;" />
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
            
            <v-row>
              <v-col v-for="group in filteredTravelReportGroups" :key="group.dep_group_id" cols="12">
                <v-card class="mx-auto my-4" elevation="12" style="border-radius: 16px;">
                  <v-card-title class="justify-center text-h5 font-weight-bold primary--text py-4">
                    <v-icon left color="primary" size="28">mdi-domain</v-icon>
                    <span class="ml-2">{{ group.dep_group_name }}</span>
                  </v-card-title>
                  <v-card-text class="text-center px-6 pb-8">
                    <div v-if="group.hasData" class="chart-container" style="height: 500px;">
                      <BarChart :chart-data="group.chartData" :chart-options="travelChartOptions" style="height: 100%; width: 100%;" />
                    </div>
                    <div v-else class="text-center py-8">
                      <v-icon size="60" color="grey lighten-2" class="mb-4">mdi-inbox-outline</v-icon>
                      <p class="grey--text text-h6 font-weight-medium">ไม่มีข้อมูลโครงการสำหรับกลุ่มงานนี้</p>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
             <v-row v-if="!travelReportGroups || travelReportGroups.length === 0">
                <v-col cols="12">
                    <v-card class="mx-auto my-8" elevation="12" max-width="1200" style="border-radius: 16px; min-height: 500px;">
                         <v-card-text class="text-center py-16">
                            <v-icon size="80" color="grey lighten-1" class="mb-4">mdi-text-box-search-outline</v-icon>
                            <h3 class="grey--text text-h5 font-weight-medium">ยังไม่มีข้อมูลรายงาน</h3>
                            <p class="grey--text text-subtitle-1 mt-2">กรุณาเลือกปีงบประมาณ</p>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>
          </div>
        </template>
      </template>
      <template v-else>
        <v-row justify="center" align="center" style="min-height: 400px;">
          <v-col cols="12" md="6" class="text-center">
            <v-card elevation="4" class="pa-8">
              <v-icon size="100" color="error" class="mb-4">mdi-account-cancel</v-icon>
              <h2 class="text-h4 font-weight-bold error--text mb-2">ไม่มีสิทธิ์เข้าถึง</h2>
              <p class="text-subtitle-1 grey--text">คุณไม่ได้รับสิทธิ์การใช้งานหน้านี้</p>
            </v-card>
          </v-col>
        </v-row>
      </template>
    </template>

    <!-- Loading Dialog -->
    <v-dialog v-model="dialogLoading" persistent max-width="400">
      <v-card style="border-radius: 16px; overflow: hidden;">
        <v-card-title class="primary white--text d-flex align-center justify-center py-6">
          <v-icon left color="white" size="28">mdi-loading mdi-spin</v-icon>
          <span class="ml-2 font-weight-bold">กำลังโหลดข้อมูล</span>
        </v-card-title>
        <v-card-text class="text-center py-8">
          <v-progress-circular :size="70" :width="7" :color="appColor" indeterminate class="mb-4"></v-progress-circular>
          <div class="loading-dots">
            <span class="font-weight-medium text-h6 primary--text">กรุณารอสักครู่</span>
            <div class="dots-container mt-3">
              <span class="dot"></span>
              <span class="dot"></span>
              <span class="dot"></span>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { Bar, Line, Doughnut, Pie, Radar, PolarArea } from 'vue-chartjs';
import { Chart as ChartJS, Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale, PointElement, LineElement, ArcElement, RadialLinearScale } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { mapGetters, mapActions } from 'vuex';
import Const from "@/const/Const";
import axios from 'axios';



ChartJS.register(
  Title, Tooltip, Legend,
  BarElement, CategoryScale, LinearScale,
  PointElement, LineElement,
  ArcElement, RadialLinearScale,
  ChartDataLabels
);

export default {
  name: 'ReportProject',
  components: {
    BarChart: Bar,
    LineChart: Line,
    DoughnutChart: Doughnut,
    PieChart: Pie,
    RadarChart: Radar,
    PolarAreaChart: PolarArea,
  },
  data() {
    return {
      isProject: true,
      projectData: [],
      chartType: 'bar',
      statusChartType: 'pie',
      budgetChartType: 'bar',
      categoryChartType: 'pie',
      categoryBudgetChartType: 'bar',
      chartTypes: [
        { type: 'bar', icon: 'mdi-chart-bar', label: 'Bar Chart', shortLabel: 'Bar' },
        { type: 'line', icon: 'mdi-chart-line', label: 'Line Chart', shortLabel: 'Line' },
        { type: 'doughnut', icon: 'mdi-chart-donut', label: 'Doughnut Chart', shortLabel: 'Donut' },
        { type: 'pie', icon: 'mdi-chart-pie', label: 'Pie Chart', shortLabel: 'Pie' },
        { type: 'radar', icon: 'mdi-radar', label: 'Radar Chart', shortLabel: 'Radar' },
        { type: 'polarArea', icon: 'mdi-chart-bell-curve-cumulative', label: 'Polar Area Chart', shortLabel: 'Polar' },
      ],
      chartData: {
        labels: ['มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน'],
        datasets: [
          {
            label: 'จำนวนโครงการ',
            backgroundColor: '#f87979',
            data: [12, 19, 3, 5, 2, 3],
            borderColor: '#f87979',
            borderWidth: 1,
          },
        ],
      },
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: {
            top: 50, // เพิ่ม padding ด้านบนเพื่อไม่ให้ datalabels ถูกตัด
            bottom: 25,
            left: 15,
            right: 15
          }
        },
        plugins: {
          datalabels: {
            anchor: 'end',
            align: 'top',
            color: '#333',
            font: {
              weight: 'bold'
            },
            formatter: function(value) {
              return value;
            }
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            // เพิ่ม grace เพื่อให้มีช่องว่างด้านบน
            grace: '25%',
            // กำหนด step size แบบไดนามิก
            ticks: {
              stepSize: function(context) {
                const datasets = context.chart.data.datasets;
                let max = 0;
                datasets.forEach(dataset => {
                  const dataMax = Math.max(...dataset.data);
                  if (dataMax > max) max = dataMax;
                });
                
                if (max >= 1000) {
                  return Math.ceil(max / 8) / 100 * 100; // แบ่ง 8 ขั้น หน่วยร้อย
                } else if (max >= 100) {
                  return Math.ceil(max / 10) / 10 * 10; // แบ่ง 10 ขั้น หน่วยสิบ
                } else {
                  return Math.ceil(max / 8); // แบ่ง 8 ขั้น
                }
              },
              maxTicksLimit: 10,
              color: '#666',
              font: {
                size: 12,
                weight: '500'
              }
            },
            // เพิ่ม grid lines ให้สวยงาม
            grid: {
              color: 'rgba(0, 0, 0, 0.08)',
              lineWidth: 1,
              drawBorder: false,
              drawTicks: false
            },
            border: {
              display: false
            }
          },
          x: {
            ticks: {
              color: '#666',
              font: {
                size: 12,
                weight: '500'
              }
            },
            grid: {
              display: false
            },
            border: {
              display: false
            }
          }
        },
      },
      // เพิ่มตัวแปรที่ใช้ใน methods และ mounted
      page: null,
      dialogProcess: false,
      dialogChooseProcess: false,
      dialogLoading: false,
      maintain: false,
      pageMessage: 'ปิดปรับปรุงระบบชั่วคราว',
      CheckAdmin: false,
      CheckSubAdmin: false,
      FiscalYearDataSel: [],
      FiscalYear: null,
      dialogDepartmentReport: false,
      departmentGroups: [],
      searchDepartment: '',
      selectedFiscalYear: null,
      travelReportGroups: [],
      searchTravel: '',
      top10ChartType: 'bar',
    };
  },
  computed: {
    filteredDepartmentGroups() {
      if (!this.searchDepartment) {
        return this.departmentGroups;
      }
      return this.departmentGroups.filter(group => 
        group.dep_group_name.toLowerCase().includes(this.searchDepartment.toLowerCase()) ||
        group.departments.some(dept => 
          dept.dep_name && dept.dep_name.toLowerCase().includes(this.searchDepartment.toLowerCase())
        )
      );
    },
    filteredTravelReportGroups() {
      if (!this.searchTravel) {
        return this.travelReportGroups;
      }
      return this.travelReportGroups.filter(group => 
        group.dep_group_name.toLowerCase().includes(this.searchTravel.toLowerCase()) ||
        group.departments.some(dept => 
          dept.dep_name && dept.dep_name.toLowerCase().includes(this.searchTravel.toLowerCase())
        )
      );
    },
    travelBudgetSummary() {
      if (!this.projectData || this.projectData.length === 0) {
        return { totalDisbursed: 0, remaining: 0 };
      }
      const totalDisbursed = this.projectData.reduce((sum, project) => sum + (parseFloat(project.MoneyDisburse) || 0), 0);
      const totalApproved = this.projectData.reduce((sum, project) => sum + (parseFloat(project.BudgetArrove) || 0), 0);
      return { totalDisbursed, remaining: totalApproved - totalDisbursed };
    },
    top10DisbursedChartData() {
      if (!this.depart || this.depart.length === 0 || !this.projectData || this.projectData.length === 0) {
        return {
          labels: ['ไม่มีข้อมูล'],
          datasets: [{
            label: 'ไม่มีข้อมูล',
            data: [0],
            backgroundColor: ['#e0e0e0'],
          }]
        };
      }

      const departmentSpending = this.depart.map(dept => {
        const disbursed = this.projectData
          .filter(p => p.Dep_Code === dept.dep_id)
          .reduce((sum, p) => sum + (parseFloat(p.MoneyDisburse) || 0), 0);
        return { name: dept.dep_name, disbursed };
      });

      const top10 = departmentSpending.sort((a, b) => b.disbursed - a.disbursed).slice(0, 10);

      return {
        labels: top10.map(d => d.name),
        datasets: [{
          label: 'งบประมาณที่เบิกจ่าย',
          data: top10.map(d => d.disbursed),
          backgroundColor: '#FF5722',
          borderColor: '#D84315',
          borderWidth: 1
        }]
      };
    },
    pieCompatibleChartTypes() {
      return this.chartTypes.filter(type => 
        ['pie', 'doughnut', 'polarArea'].includes(type.type)
      );
    },
    chartComponent() {
      switch (this.chartType) {
        case 'bar':
          return 'BarChart';
        case 'line':
          return 'LineChart';
        case 'doughnut':
          return 'DoughnutChart';
        case 'pie':
          return 'PieChart';
        case 'radar':
          return 'RadarChart';
        case 'polarArea':
          return 'PolarAreaChart';
        default:
          return 'BarChart';
      }
    },
    yStepSize() {
      const data = this.chartData?.datasets?.[0]?.data || [];
      const max = Math.max(...data, 0);
      if (max >= 10000000) {
        return Math.ceil(max / 10 / 10000000) * 10000000;
      } else if (max >= 1000000) {
        return Math.ceil(max / 10 / 1000000) * 1000000;
      } else if (max >= 100000) {
        return Math.ceil(max / 10 / 100000) * 100000;
      } else if (max >= 10000) {
        return Math.ceil(max / 10 / 10000) * 10000;
      } else if (max >= 1000) {
        return Math.ceil(max / 10 / 1000) * 1000;
      } else {
        return Math.ceil(max / 10) || 1;
      }
    },
    // Chart ที่ 1: สรุปสถานะโครงการ
    projectStatusChartData() {
      if (!this.projectData || this.projectData.length === 0) {
        return {
          labels: ['ไม่มีข้อมูล'],
          datasets: [{
            data: [1],
            backgroundColor: ['#e0e0e0'],
          }]
        };
      }
      
      const arranged = this.projectData.filter(project => project.expense && project.expense.length > 0).length;
      const notArranged = this.projectData.filter(project => !project.expense || project.expense.length === 0).length;
      
      return {
        labels: ['จัดแล้ว', 'ยังไม่จัด'],
        datasets: [{
          data: [arranged, notArranged],
          backgroundColor: ['#4CAF50', '#FF9800'],
          borderWidth: 2,
          borderColor: '#fff'
        }]
      };
    },
    // Chart ที่ 2: งบประมาณ
    budgetChartData() {
      if (!this.projectData || this.projectData.length === 0) {
        return {
          labels: ['ไม่มีข้อมูล'],
          datasets: [{
            label: 'ไม่มีข้อมูล',
            data: [0],
            backgroundColor: ['#e0e0e0'],
          }]
        };
      }
      
      const totalApproved = this.projectData.reduce((sum, project) => sum + (parseFloat(project.BudgetArrove) || 0), 0);
      const totalDisbursed = this.projectData.reduce((sum, project) => sum + (parseFloat(project.MoneyDisburse) || 0), 0);
      
      return {
        labels: ['งบประมาณที่อนุมัติ', 'งบประมาณที่เบิกจ่าย'],
        datasets: [{
          label: 'จำนวนเงิน (บาท)',
          data: [totalApproved, totalDisbursed],
          backgroundColor: ['#2196F3', '#FF5722'],
          borderColor: ['#1976D2', '#D84315'],
          borderWidth: 1
        }]
      };
    },
    // Chart Options
    pieChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          datalabels: {
            color: '#000',
            font: {
              weight: 'bold',
              size: 16
            },
            formatter: (value, context) => {
              const total = context.dataset.data.reduce((a, b) => a + b, 0);
              const percentage = ((value / total) * 100).toFixed(1);
              const formattedValue = new Intl.NumberFormat('th-TH').format(value) + ' บาท';
              return `${formattedValue}\n(${percentage}%)`;
            }
          },
          legend: {
            position: 'bottom'
          }
        }
      };
    },
    barChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: {
            top: 50, // เพิ่ม padding ด้านบนเพื่อไม่ให้ datalabels ถูกตัด
            bottom: 25,
            left: 15,
            right: 15
          }
        },
        plugins: {
          datalabels: {
            anchor: 'end',
            align: 'top',
            color: '#333',
            font: {
              weight: 'bold',
              size: 14
            },
            formatter: function(value) {
              // return new Intl.NumberFormat('th-TH').format(value) + ' บาท';
              return new Intl.NumberFormat('th-TH').format(value) + '';
            }
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            // เพิ่ม grace เพื่อให้มีช่องว่างด้านบน
            grace: '25%',
            // กำหนด step size แบบไดนามิก
            ticks: {
              stepSize: function(context) {
                const datasets = context.chart.data.datasets;
                let max = 0;
                datasets.forEach(dataset => {
                  const dataMax = Math.max(...dataset.data);
                  if (dataMax > max) max = dataMax;
                });
                
                if (max >= 100000000) {
                  return Math.ceil(max / 8) / 10000000 * 10000000; // หน่วยสิบล้าน แบ่ง 8 ขั้น
                } else if (max >= 10000000) {
                  return Math.ceil(max / 10) / 1000000 * 1000000; // หน่วยล้าน แบ่ง 10 ขั้น
                } else if (max >= 1000000) {
                  return Math.ceil(max / 8) / 100000 * 100000; // หน่วยแสน แบ่ง 8 ขั้น
                } else if (max >= 100000) {
                  return Math.ceil(max / 10) / 10000 * 10000; // หน่วยหมื่น แบ่ง 10 ขั้น
                } else if (max >= 10000) {
                  return Math.ceil(max / 8) / 1000 * 1000; // หน่วยพัน แบ่ง 8 ขั้น
                } else if (max >= 1000) {
                  return Math.ceil(max / 10) / 100 * 100; // หน่วยร้อย แบ่ง 10 ขั้น
                } else {
                  return Math.ceil(max / 8); // หน่วยทั่วไป แบ่ง 8 ขั้น
                }
              },
              maxTicksLimit: 10, // จำกัดจำนวน ticks สูงสุด
              callback: function(value) {
                // return new Intl.NumberFormat('th-TH').format(value) + ' บาท';
                return new Intl.NumberFormat('th-TH').format(value) + '';
              },
              color: '#666',
              font: {
                size: 12,
                weight: '500'
              }
            },
            // เพิ่ม grid lines ให้สวยงาม
            grid: {
              color: 'rgba(0, 0, 0, 0.08)',
              lineWidth: 1,
              drawBorder: false,
              drawTicks: false
            },
            border: {
              display: false
            }
          },
          x: {
            ticks: {
              color: '#666',
              font: {
                size: 12,
                weight: '500'
              }
            },
            grid: {
              display: false
            },
            border: {
              display: false
            }
          }
        }
      };
    },
    smallPieChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          datalabels: {
            color: '#000',
            font: {
              weight: 'bold',
              size: 12
            },
            formatter: (value, context) => {
              const total = context.dataset.data.reduce((a, b) => a + b, 0);
              const percentage = ((value / total) * 100).toFixed(1);
              console.log(value, total, percentage);
              return `${value}`;
            }
          },
          legend: {
            position: 'bottom',
            labels: {
              font: {
                size: 10
              }
            }
          }
        }
      };
    },
    smallBarChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: {
            top: 25, // เพิ่ม padding สำหรับ small charts
            bottom: 15,
            left: 8,
            right: 8
          }
        },
        plugins: {
          datalabels: {
            display: false
          },
          legend: {
            display: false
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            // เพิ่ม grace สำหรับ small charts
            grace: '20%',
            ticks: {
              stepSize: function(context) {
                const datasets = context.chart.data.datasets;
                let max = 0;
                datasets.forEach(dataset => {
                  const dataMax = Math.max(...dataset.data);
                  if (dataMax > max) max = dataMax;
                });
                
                if (max >= 10000000) {
                  return Math.ceil(max / 5) / 1000000 * 1000000; // หน่วยล้าน แบ่ง 5 ขั้น
                } else if (max >= 1000000) {
                  return Math.ceil(max / 6) / 100000 * 100000; // หน่วยแสน แบ่ง 6 ขั้น
                } else if (max >= 100000) {
                  return Math.ceil(max / 5) / 10000 * 10000; // หน่วยหมื่น แบ่ง 5 ขั้น
                } else if (max >= 10000) {
                  return Math.ceil(max / 6) / 1000 * 1000; // หน่วยพัน แบ่ง 6 ขั้น
                } else {
                  return Math.ceil(max / 5); // แบ่ง 5 ขั้น
                }
              },
              maxTicksLimit: 6, // จำกัดสำหรับ small charts
              font: {
                size: 10
              },
              callback: function(value) {
                // return new Intl.NumberFormat('th-TH').format(value) + ' บาท';
                return new Intl.NumberFormat('th-TH').format(value) + '';
              },
              color: '#666'
            },
            grid: {
              color: 'rgba(0, 0, 0, 0.06)',
              lineWidth: 1,
              drawBorder: false,
              drawTicks: false
            },
            border: {
              display: false
            }
          },
          x: {
            ticks: {
              font: {
                size: 10
              },
              color: '#666'
            },
            grid: {
              display: false
            },
            border: {
              display: false
            }
          }
        }
      };
    },
    travelChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: {
            top: 50,
            bottom: 25,
            left: 15,
            right: 15
          }
        },
        plugins: {
          datalabels: {
            anchor: 'end',
            align: 'top',
            color: '#333',
            font: {
              weight: 'bold',
              size: 14
            },
            formatter: function(value) {
              // return new Intl.NumberFormat('th-TH').format(value) + ' บาท';
              return new Intl.NumberFormat('th-TH').format(value) + '';
            }
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            grace: '25%',
            ticks: {
              stepSize: function(context) {
                const datasets = context.chart.data.datasets;
                let max = 0;
                datasets.forEach(dataset => {
                  const dataMax = Math.max(...dataset.data);
                  if (dataMax > max) max = dataMax;
                });
                
                if (max >= 100000000) {
                  return Math.ceil(max / 8) / 10000000 * 10000000;
                } else if (max >= 10000000) {
                  return Math.ceil(max / 10) / 1000000 * 1000000;
                } else if (max >= 1000000) {
                  return Math.ceil(max / 8) / 100000 * 100000;
                } else if (max >= 100000) {
                  return Math.ceil(max / 10) / 10000 * 10000;
                } else if (max >= 10000) {
                  return Math.ceil(max / 8) / 1000 * 1000;
                } else if (max >= 1000) {
                  return Math.ceil(max / 10) / 100 * 100;
                } else {
                  return Math.ceil(max / 8);
                }
              },
              maxTicksLimit: 10,
              callback: function(value) {
                // return new Intl.NumberFormat('th-TH').format(value) + ' บาท';
                return new Intl.NumberFormat('th-TH').format(value) + '';
              },
              color: '#666',
              font: {
                size: 12,
                weight: '500'
              }
            },
            grid: {
              color: 'rgba(0, 0, 0, 0.08)',
              lineWidth: 1,
              drawBorder: false,
              drawTicks: false
            },
            border: {
              display: false
            }
          },
          x: {
            ticks: {
              color: '#666',
              font: {
                size: 12,
                weight: '500'
              }
            },
            grid: {
              display: false
            },
            border: {
              display: false
            }
          }
        }
      };
    },
    ...mapGetters({
      MeData: 'Staff/getMeData',
      AllDepPermission: 'Permission/getAllDepUserPermission',
      DepPermission: 'Permission/getDepUserPermission',
      PermissionAdmin: 'Permission/getPermission',
      Permission: 'Permission/getPermissionUser',
      Permission_Ms: 'Permission/getPermissionMs',
      Permission_Dep_User: 'Permission/getPermissionDepUser',
      Permission_Dep: 'Permission/getPermissionDep',
      Approve_Ms: 'Permission/getApproveMs',
      Approve: 'Permission/getApprove',
      MenuPermiss: 'Permission/getMenuPermiss',
      IgnoreMenuPermiss: 'Permission/getIgnoreMenuPermiss',
      TypeProject: 'Project/getTypeProject',
      HeaderPage: 'Project/getHeaderPage',
      JoinProjectPage: 'Project/getJoinProjectPage',
      appColor: 'Page/getColor',
      appHeaderName: 'Page/getHeaderName',
      depart: 'Depart/getDepart',
    })
  },
  methods: {
    ...mapActions({
      initMeData: 'Staff/initMeData',
      initPermissionDepUserAllData: 'Permission/initPermissionDepUserAllData',
      initPermissionDepUserData: 'Permission/initPermissionDepUserData',
      initPermissionUserData: 'Permission/initPermissionUserData',
      initPermissionMsData: 'Permission/initPermissionMsData',
      getPermissionUserData: 'Permission/getPermissionUserData',
      getPermissionDep: 'Permission/getPermissionDepData',
      initPermissionDepData: 'Permission/initPermissionDepData',
      getPermissionDepUserData: 'Permission/getPermissionDepUserData',
      initApproveMsData: 'Permission/initApproveMsData',
      initApproveData: 'Permission/initApproveData',
      getPermissionData: 'Permission/getPermissionData',
      initPermissionData: 'Permission/initPermissionData',
      getMenuPermissData: 'Permission/getMenuPermissData',
      getIgnoreMenuPermissData: 'Permission/getIgnoreMenuPermissData',
      changeTypeProject: 'Project/changeTypeProject',
      changeHeaderPage: 'Project/changeHeaderPage',
      changeJoinProjectPage: 'Project/changeJoinProjectPage',
      changeColor: 'Page/changeColor',
      changeHeaderName: 'Page/changeHeaderName',
      initDepartData: 'Depart/initDepartData',

    }),
    // Format currency for display
    formatCurrency(value) {
      if (value >= 1000000000) {
        return (value / 1000000000).toFixed(1) + 'B บาท';
      } else if (value >= 1000000) {
        return (value / 1000000).toFixed(1) + 'M บาท';
      } else if (value >= 1000) {
        return (value / 1000).toFixed(0) + 'K บาท';
      }
      // return new Intl.NumberFormat('th-TH').format(value) + ' บาท';
      return new Intl.NumberFormat('th-TH').format(value) + '';
    },
    clearDepartmentSearch() {
      this.searchDepartment = '';
    },
    // Handle fiscal year change
    onFiscalYearChange() {
      if (this.isProject) {
        this.loadProjectData();
      } else {
        this.loadTravelData();
      }
    },
    // Load project data based on selected fiscal year
    loadProjectData() {
      this.projectData = [];
      this.dialogLoading = true;
      
      const fiscalYear = this.selectedFiscalYear || (new Date().getMonth() + 1 > 10 ? new Date().getFullYear() + 1 : new Date().getFullYear());
      
      axios.post(Const.ConnectionHost + "/HrTrain2/serviceAPI.php", {
        queryName: "getSummaryReportData",
        param: btoa(
          encodeURIComponent(
            JSON.stringify({
              permiss_check: this.MeData[0].perid,
              Project_type: this.TypeProject == null ? 'all' : this.TypeProject,
              FiscalYear: fiscalYear
            })
          )
        ),
      })
        .then((res) => {
          if (res.data.length > 0) {
            this.projectData = res.data;
            this.prepareDepartmentGroups();
          } else {
            console.log("No data found for fiscal year:", fiscalYear);
            this.prepareDepartmentGroups();
          }
        })
        .catch((res) => {
          console.log(res);
        })
        .finally(() => {
          this.dialogLoading = false;
        });
    },
    loadTravelData() {
      this.travelReportGroups = [];
      this.dialogLoading = true;

      const fiscalYear = this.selectedFiscalYear || (new Date().getMonth() + 1 > 10 ? new Date().getFullYear() + 1 : new Date().getFullYear());

      axios.post(Const.ConnectionHost + "/HrTrain2/serviceAPI.php", {
        queryName: "getSummaryReportData",
        param: btoa(
          encodeURIComponent(
            JSON.stringify({
              permiss_check: this.MeData[0].perid,
              Project_type: 'all',
              FiscalYear: fiscalYear
            })
          )
        ),
      })
      .then((res) => {
        if (res.data.length > 0) {
          this.prepareTravelReportGroups(res.data);
        } else {
          console.log("No data found for fiscal year:", fiscalYear);
          this.prepareTravelReportGroups([]);
        }
      })
      .catch((res) => {
        console.log(res);
      })
      .finally(() => {
        this.dialogLoading = false;
      });
    },
    // Format full currency
    formatFullCurrency(value) {
      // return new Intl.NumberFormat('th-TH').format(value) + ' บาท';
      return new Intl.NumberFormat('th-TH').format(value) + '';
    },
    // Get chart component based on type
    getChartComponent(type) {
      switch (type) {
        case 'bar':
          return 'BarChart';
        case 'line':
          return 'LineChart';
        case 'doughnut':
          return 'DoughnutChart';
        case 'pie':
          return 'PieChart';
        case 'radar':
          return 'RadarChart';
        case 'polarArea':
          return 'PolarAreaChart';
        default:
          return 'BarChart';
      }
    },
    // Dynamic chart options based on type and context
    getChartOptions(chartType, context) {
      const baseOptions = {
        responsive: true,
        maintainAspectRatio: false,
      };

      const isSmall = context.includes('small');
      const isMini = context.includes('mini');
      const fontSize = isMini ? 8 : (isSmall ? 10 : 14);

      if (['pie', 'doughnut', 'polarArea'].includes(chartType)) {
        return {
          ...baseOptions,
          plugins: {
            datalabels: {
              color: '#000',
              font: {
                weight: 'bold',
                size: isSmall || isMini ? 14 : 16
              },
              formatter: (value, ctx) => {
                // Format currency for budget context in all cases (small, mini, regular)
                if (context.includes('budget') || context.includes('Budget') || context.includes('categoryBudget')) {
                  if (isSmall || isMini) {
                    // return new Intl.NumberFormat('th-TH').format(value) + ' บาท';
                    return new Intl.NumberFormat('th-TH').format(value) + '';
                  }
                  const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
                  const percentage = ((value / total) * 100).toFixed(1);
                  // return `${new Intl.NumberFormat('th-TH').format(value)} บาท\n(${percentage}%)`;
                  return `${new Intl.NumberFormat('th-TH').format(value)}\n(${percentage}%)`;
                }
                
                // For category (project count) charts - don't add บาท
                if (context.includes('category')) {
                  if (isSmall || isMini) {
                    return value;
                  }
                  const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
                  const percentage = ((value / total) * 100).toFixed(1);
                  return `${value}\n(${percentage}%)`;
                }
                
                if (isSmall || isMini) {
                  return value;
                }
                const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
                const percentage = ((value / total) * 100).toFixed(1);
                return `${value}\n(${percentage}%)`;
              }
            },
            legend: {
              position: 'bottom'
            }
          }
        };
      } else if (chartType === 'radar') {
        return {
          ...baseOptions,
          plugins: {
            datalabels: {
              display: !isSmall && !isMini,
              color: '#333',
              font: {
                weight: 'bold',
                size: fontSize
              }
            },
            legend: {
              position: 'bottom',
              labels: {
                font: {
                  size: fontSize
                }
              }
            }
          },
          scales: {
            r: {
              beginAtZero: true,
              ticks: {
                font: {
                  size: fontSize
                }
              }
            }
          }
        };
      } else {
        // Bar and Line charts
        return {
          ...baseOptions,
          layout: {
            padding: {
              top: 40, // เพิ่ม padding ด้านบนเพื่อไม่ให้ datalabels ถูกตัด
              bottom: 20,
              left: 10,
              right: 10
            }
          },
          plugins: {
            datalabels: {
              display: !isSmall && !isMini,
              anchor: 'end',
              align: 'top',
              color: '#333',
              font: {
                weight: 'bold',
                size: fontSize
              },
              formatter: function(value) {
                // Format currency for budget context
                if (context.includes('budget') || context.includes('Budget') || context.includes('categoryBudget')) {
                  // return new Intl.NumberFormat('th-TH').format(value) + ' บาท';
                  return new Intl.NumberFormat('th-TH').format(value) + '';
                }
                // For category (project count) charts - don't add บาท
                if (context.includes('category')) {
                  return new Intl.NumberFormat('th-TH').format(value);
                }
                return new Intl.NumberFormat('th-TH').format(value);
              }.bind(this)
            },
            legend: {
              display: chartType === 'line' || context.includes('budget') || context.includes('category'),
              position: 'bottom',
              labels: {
                font: {
                  size: fontSize
                }
              }
            }
          },
          scales: {
            y: {
              beginAtZero: true,
              // เพิ่ม padding เพื่อไม่ให้ datalabels ถูกตัด
              suggestedMax: function(context) {
                const max = Math.max(...context.chart.data.datasets[0].data);
                return max * 1.25; // เพิ่ม 25% ของค่าสูงสุด
              },
              grace: '20%', // เพิ่ม grace เพื่อให้มีช่องว่างด้านบน
              // กำหนด step size สำหรับการแบ่งขั้น
              ticks: {
                stepSize: this.yStepSize,
                maxTicksLimit: 8, // จำกัดจำนวน ticks สูงสุด
                font: {
                  size: fontSize
                },
                callback: function(value) {
                  if (isSmall || isMini) {
                    if (context.includes('budget') || context.includes('Budget') || context.includes('categoryBudget')) {
                      // return new Intl.NumberFormat('th-TH').format(value) + ' บาท';
                      return new Intl.NumberFormat('th-TH').format(value) + '';
                    }
                    if (context.includes('category')) {
                      return new Intl.NumberFormat('th-TH').format(value);
                    }
                    return new Intl.NumberFormat('th-TH', {
                      notation: 'compact',
                      compactDisplay: 'short'
                    }).format(value);
                  }
                  if (context.includes('budget') || context.includes('Budget') || context.includes('categoryBudget')) {
                    // return new Intl.NumberFormat('th-TH').format(value) + ' บาท';
                    return new Intl.NumberFormat('th-TH').format(value) + '';
                  }
                  if (context.includes('category')) {
                    return new Intl.NumberFormat('th-TH').format(value);
                  }
                  return new Intl.NumberFormat('th-TH').format(value);
                }.bind(this)
              },
              // เพิ่ม grid lines ให้สวยงาม
              grid: {
                color: 'rgba(0, 0, 0, 0.08)',
                lineWidth: 1,
                drawBorder: false,
                drawTicks: false
              }
            },
            x: {
              ticks: {
                font: {
                  size: fontSize
                }
              }
            }
          }
        };
      }
    },
    // Dynamic project status chart data based on chart type
    getProjectStatusChartData(chartType) {
      if (!this.projectData || this.projectData.length === 0) {
        return {
          labels: ['ไม่มีข้อมูล'],
          datasets: [{
            data: [1],
            backgroundColor: ['#e0e0e0'],
          }]
        };
      }
      
      const arranged = this.projectData.filter(project => project.expense && project.expense.length > 0).length;
      const notArranged = this.projectData.filter(project => !project.expense || project.expense.length === 0).length;
      
      const baseData = {
        labels: ['จัดแล้ว', 'ยังไม่จัด'],
        datasets: [{
          label: 'จำนวนโครงการ',
          data: [arranged, notArranged],
          backgroundColor: ['#4CAF50', '#FF9800'],
          borderColor: ['#2E7D32', '#F57C00'],
          borderWidth: 2
        }]
      };

      if (['line', 'radar'].includes(chartType)) {
        baseData.datasets[0].fill = chartType === 'radar';
        baseData.datasets[0].tension = 0.4;
      }

      return baseData;
    },
    // Dynamic budget chart data based on chart type
    getBudgetChartData(chartType) {
      if (!this.projectData || this.projectData.length === 0) {
        return {
          labels: ['ไม่มีข้อมูล'],
          datasets: [{
            label: 'ไม่มีข้อมูล',
            data: [0],
            backgroundColor: ['#e0e0e0'],
          }]
        };
      }
      
      const totalApproved = this.projectData.reduce((sum, project) => sum + (parseFloat(project.BudgetArrove) || 0), 0);
      const totalDisbursed = this.projectData.reduce((sum, project) => sum + (parseFloat(project.MoneyDisburse) || 0), 0);
      
      const baseData = {
        labels: ['งบประมาณที่อนุมัติ', 'งบประมาณที่เบิกจ่าย'],
        datasets: [{
          label: 'จำนวนเงิน (บาท)',
          data: [totalApproved, totalDisbursed],
          backgroundColor: ['#2196F3', '#FF5722'],
          borderColor: ['#1976D2', '#D84315'],
          borderWidth: 2
        }]
      };

      if (['line', 'radar'].includes(chartType)) {
        baseData.datasets[0].fill = chartType === 'radar';
        baseData.datasets[0].tension = 0.4;
      }

      return baseData;
    },
    
    // Chart data for project categories
    getCategoryChartData(chartType) {
      if (!this.projectData || this.projectData.length === 0) {
        return {
          labels: ['ไม่มีข้อมูล'],
          datasets: [{
            data: [1],
            backgroundColor: ['#e0e0e0'],
          }]
        };
      }

      // แยกโครงการตามประเภท 1-4
      const categories = {
        1: 'พัฒนาบุคลากร',
        2: 'โครงการอื่น ๆ',
        3: 'บริการวิชาการ',
        4: 'วิจัย'
      };

      const categoryCounts = {};
      Object.keys(categories).forEach(key => {
        categoryCounts[key] = this.projectData.filter(project => 
          project.Project_category && project.Project_category.toString() === key
        ).length;
      });

      // สีสำหรับแต่ละประเภท
      const colors = ['#4CAF50', '#2196F3', '#FF9800', '#E91E63'];
      
      const baseData = {
        labels: Object.values(categories),
        datasets: [{
          label: 'จำนวนโครงการ',
          data: Object.values(categoryCounts),
          backgroundColor: colors,
          borderColor: colors,
          borderWidth: 2
        }]
      };

      // ปรับแต่งตาม chart type
      if (['line', 'radar'].includes(chartType)) {
        baseData.datasets[0].fill = chartType === 'radar';
        baseData.datasets[0].tension = 0.4;
      }

      return baseData;
    },

    // Chart data for category budget
    getCategoryBudgetChartData(chartType) {
      if (!this.projectData || this.projectData.length === 0) {
        return {
          labels: ['ไม่มีข้อมูล'],
          datasets: [{
            label: 'ไม่มีข้อมูล',
            data: [0],
            backgroundColor: ['#e0e0e0'],
          }]
        };
      }

      // แยกงบประมาณตามประเภท 1-4
      const categories = {
        1: 'พัฒนาบุคลากร',
        2: 'โครงการอื่น ๆ',
        3: 'บริการวิชาการ', 
        4: 'วิจัย'
      };

      const approvedBudget = {};
      const disbursedBudget = {};

      Object.keys(categories).forEach(key => {
        const categoryProjects = this.projectData.filter(project => 
          project.Project_category && project.Project_category.toString() === key
        );
        
        approvedBudget[key] = categoryProjects.reduce((sum, project) => 
          sum + (parseFloat(project.BudgetArrove) || 0), 0
        );
        
        disbursedBudget[key] = categoryProjects.reduce((sum, project) => 
          sum + (parseFloat(project.MoneyDisburse) || 0), 0
        );
      });

      if (['pie', 'doughnut', 'polarArea'].includes(chartType)) {
        // สำหรับ pie chart จะแสดงเฉพาะงบประมาณที่อนุมัติ
        return {
          labels: Object.values(categories),
          datasets: [{
            data: Object.values(approvedBudget),
            backgroundColor: ['#FFB74D', '#81C784', '#64B5F6', '#F06292'],
            borderWidth: 2,
            borderColor: '#fff'
          }]
        };
      } else {
        const baseData = {
          labels: Object.values(categories),
          datasets: [
            {
              label: 'งบประมาณที่อนุมัติ',
              data: Object.values(approvedBudget),
              backgroundColor: ['#FF9800', '#4CAF50', '#2196F3', '#E91E63'],
              borderColor: ['#F57C00', '#388E3C', '#1976D2', '#C2185B'],
              borderWidth: 2
            },
            {
              label: 'งบประมาณที่เบิกจ่าย',
              data: Object.values(disbursedBudget),
              backgroundColor: ['#FFCC02', '#8BC34A', '#03DAC6', '#FF4081'],
              borderColor: ['#FFA000', '#689F38', '#00ACC1', '#E91E63'],
              borderWidth: 2
            }
          ]
        };

        // ปรับแต่งตาม chart type
        if (['line', 'radar'].includes(chartType)) {
          baseData.datasets.forEach(dataset => {
            dataset.fill = chartType === 'radar';
            dataset.tension = 0.4;
          });
        }

        return baseData;
      }
    },
    
    init() {
      this.initDepartData().then((res) => {
        if (res) {
          this.loadProjectData();
        } else {
          console.error("Failed to initialize department data");
          this.dialogLoading = false;
        }
      }).catch(err => {
        console.error("Error initializing department data:", err);
        this.dialogLoading = false;
      });
    },
    prepareDepartmentGroups() {
      if (!this.depart || this.depart.length === 0) {
        this.departmentGroups = [];
        return;
      }

      // จัดกลุ่มหน่วยงานตาม dep_group_id
      const groups = {};
      this.depart.forEach(dept => {
        if (!groups[dept.dep_group_id]) {
          groups[dept.dep_group_id] = {
            dep_group_id: dept.dep_group_id,
            dep_group_name: dept.dep_group_name || `กลุ่มที่ ${dept.dep_group_id}`,
            departments: [],
            hasData: false,
            statusChartData: null,
            budgetChartData: null,
            statusChartType: 'pie',
            budgetChartType: 'bar'
          };
        }
        groups[dept.dep_group_id].departments.push(dept);
      });

      // คำนวณข้อมูลสำหรับแต่ละกลุ่ม
      Object.values(groups).forEach(group => {
        const groupProjects = this.projectData.filter(project => 
          group.departments.some(dept => dept.dep_id === project.Dep_Code)
        );

        if (groupProjects.length > 0) {
          group.hasData = true;
          
          // สถานะโครงการ
          const arranged = groupProjects.filter(project => project.expense && project.expense.length > 0).length;
          const notArranged = groupProjects.filter(project => !project.expense || project.expense.length === 0).length;
          
          group.statusChartData = {
            labels: ['จัดแล้ว', 'ยังไม่จัด'],
            datasets: [{
              data: [arranged, notArranged],
              backgroundColor: ['#4CAF50', '#FF9800'],
              borderWidth: 1,
              borderColor: '#fff'
            }]
          };

          // งบประมาณ
          const totalApproved = groupProjects.reduce((sum, project) => sum + (parseFloat(project.BudgetArrove) || 0), 0);
          const totalDisbursed = groupProjects.reduce((sum, project) => sum + (parseFloat(project.MoneyDisburse) || 0), 0);
          
          group.budgetChartData = {
            labels: ['อนุมัติ', 'เบิกจ่าย'],
            datasets: [{
              label: 'จำนวนเงิน (บาท)',
              data: [totalApproved, totalDisbursed],
              backgroundColor: ['#2196F3', '#FF5722'],
              borderColor: ['#1976D2', '#D84315'],
              borderWidth: 1
            }]
          };
        }
      });

      this.departmentGroups = Object.values(groups);
    },
    prepareTravelReportGroups(data) {
      if (!this.depart || this.depart.length === 0) {
        this.travelReportGroups = [];
        return;
      }

      const groups = {};
      this.depart.forEach(dept => {
        if (!groups[dept.dep_group_id]) {
          groups[dept.dep_group_id] = {
            dep_group_id: dept.dep_group_id,
            dep_group_name: dept.dep_group_name || `กลุ่มที่ ${dept.dep_group_id}`,
            departments: [],
            hasData: false,
            chartData: null
          };
        }
        groups[dept.dep_group_id].departments.push(dept);
      });

      Object.values(groups).forEach(group => {
        const labels = group.departments.map(d => d.dep_name);
        const approvedBudgets = [];
        const disbursedBudgets = [];

        group.departments.forEach(dept => {
          const deptProjects = data.filter(p => p.Dep_Code === dept.dep_id);
          const approved = deptProjects.reduce((sum, p) => sum + (parseFloat(p.BudgetArrove) || 0), 0);
          const disbursed = deptProjects.reduce((sum, p) => sum + (parseFloat(p.MoneyDisburse) || 0), 0);
          approvedBudgets.push(approved);
          disbursedBudgets.push(disbursed);
        });

        if (approvedBudgets.some(b => b > 0) || disbursedBudgets.some(b => b > 0)) {
          group.hasData = true;
          group.chartData = {
            labels: labels,
            datasets: [
              // {
              //   label: 'งบประมาณที่อนุมัติ',
              //   data: approvedBudgets,
              //   backgroundColor: '#2196F3',
              //   borderColor: '#1976D2',
              //   borderWidth: 1
              // },
              {
                label: 'งบประมาณที่เบิกจ่าย',
                data: disbursedBudgets,
                backgroundColor: '#FF5722',
                borderColor: '#D84315',
                borderWidth: 1
              }
            ]
          };
        }
      });

      this.travelReportGroups = Object.values(groups);
    },
    // ฟังก์ชันสำหรับเปลี่ยนประเภทกราฟ
    changeChartType(type) {
      this.chartType = type;
      // ตัวอย่างข้อมูลสำหรับแต่ละประเภทกราฟ
      if (type === 'doughnut' || type === 'pie') {
        this.chartData = {
          labels: ['มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน'],
          datasets: [
            {
              label: 'จำนวนโครงการ',
              backgroundColor: [
                '#f87979', '#a3e635', '#60a5fa', '#fbbf24', '#f472b6', '#34d399'
              ],
              data: [12, 19, 3, 5, 2, 3],
            },
          ],
        };
      } else if (type === 'radar') {
        this.chartData = {
          labels: ['มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน'],
          datasets: [
            {
              label: 'จำนวนโครงการ',
              backgroundColor: 'rgba(248,121,121,0.2)',
              borderColor: '#f87979',
              pointBackgroundColor: '#f87979',
              data: [20, 260, 40, 30, 50, 80],
            },
          ],
        };
      } else if (type === 'polarArea') {
        this.chartData = {
          labels: ['มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน'],
          datasets: [
            {
              label: 'จำนวนโครงการ',
              backgroundColor: [
                '#f87979', '#a3e635', '#60a5fa', '#fbbf24', '#f472b6', '#34d399'
              ],
              data: [12, 19, 3, 5, 2, 3],
            },
          ],
        };
      } else {
        this.chartData = {
          labels: ['มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน'],
          datasets: [
            {
              label: 'จำนวนโครงการ',
              backgroundColor: '#f87979',
              data: [12, 19, 3, 5, 2, 3],
              borderColor: '#f87979',
              borderWidth: 1,
            },
          ],
        };
      }
    },

    // สรุปจำนวนโครงการแต่ละประเภท
    getCategorySummary() {
      if (!this.projectData || this.projectData.length === 0) {
        return [];
      }

      const categories = {
        1: 'พัฒนาบุคลากร',
        2: 'โครงการอื่น ๆ',
        3: 'บริการวิชาการ',
        4: 'วิจัย'
      };

      return Object.keys(categories).map(key => {
        const categoryProjects = this.projectData.filter(project => 
          project.Project_category && project.Project_category.toString() === key
        );
        
        const count = categoryProjects.length;
        
        const approvedBudget = categoryProjects.reduce((sum, project) => {
          return sum + (parseFloat(project.BudgetArrove) || 0);
        }, 0);
        
        const disbursedBudget = categoryProjects.reduce((sum, project) => {
          return sum + (parseFloat(project.MoneyDisburse) || 0);
        }, 0);
        
        return {
          name: categories[key],
          count: count,
          approvedBudget: approvedBudget,
          disbursedBudget: disbursedBudget
        };
      });
    },

    // สรุปงบประมาณแต่ละประเภท
    getCategoryBudgetSummary() {
      if (!this.projectData || this.projectData.length === 0) {
        return [];
      }

      const categories = {
        1: 'พัฒนาบุคลากร',
        2: 'โครงการอื่น ๆ',
        3: 'บริการวิชาการ',
        4: 'วิจัย'
      };

      return Object.keys(categories).map(key => {
        const categoryProjects = this.projectData.filter(project => 
          project.Project_category && project.Project_category.toString() === key
        );
        
        const approvedBudget = categoryProjects.reduce((sum, project) => {
          return sum + (parseFloat(project.BudgetArrove || 0));
        }, 0);
        
        const disbursedBudget = categoryProjects.reduce((sum, project) => {
          return sum + (parseFloat(project.MoneyDisburse || 0));
        }, 0);
        
        return {
          name: categories[key],
          totalBudget: disbursedBudget,  // เปลี่ยนเป็นเงินที่เบิกจ่าย
          approvedBudget: approvedBudget,
          disbursedBudget: disbursedBudget
        };
      });
    },

    // สีสำหรับแต่ละประเภท
    getCategoryColor(index) {
      const colors = ['#4CAF50', '#2196F3', '#FF9800', '#E91E63'];
      return colors[index] || '#9E9E9E';
    },

    // ไอคอนสำหรับแต่ละประเภท
    getCategoryIcon(index) {
      const icons = [
        'mdi-account-group', // พัฒนาบุคลากร
        'mdi-briefcase-variant', // โครงการอื่น ๆ
        'mdi-school', // บริการวิชาการ
        'mdi-flask' // วิจัย
      ];
      return icons[index] || 'mdi-help-circle';
    },

    ChooseProcess(page) {
      this.page = null
      this.page = page
      this.changeHeaderPage({
        page: '2'
      })
      this.changeJoinProjectPage({
        page: null
      })
      if (page == '1') {
        this.changeTypeProject({
          type: '1'
        })
        this.changeColor({
          color: '#1976d2'
        })
        this.changeHeaderName({
          headerName: Const.defaultHeaderApp1
        })
        if (this.$route.path !== '/ReportProject') {
          this.$router.push('/ReportProject')
        }

        this.dialogProcess = false
      } else {
        this.changeTypeProject({
          type: '2'
        })
        this.changeColor({
          color: '#004D40'
        })
        this.changeHeaderName({
          headerName: Const.defaultHeaderApp2
        })
        if (this.$route.path !== '/ReportProject') {
          this.$router.push('/ReportProject')
        }
        this.dialogProcess = false
      }
    },
    openDepartmentReportDialog() {
      this.dialogDepartmentReport = true;
      this.searchDepartment = '';
    },
    onReportTypeChange() {
      this.ChooseProcess(this.isProject ? '1' : '2');
      console.log(this.TypeProject)
      if (this.isProject) {
        this.loadProjectData();
      } else {
        this.loadTravelData();
      }

    },
  },
  mounted() {
    this.changeHeaderPage({
      page: '2'
    })
    this.changeColor({
      color: Const.defaultColorApp
    })
    this.changeHeaderName({
      headerName: Const.defaultHeaderApp
    })
    for (var l = 2020; l <= (new Date().getFullYear() + 3); l++) {
      this.FiscalYearDataSel.push({
        year: l,
        yearth: l + 543
      })
    }
    this.FiscalYear = new Date().getFullYear()
    this.selectedFiscalYear = new Date().getMonth() + 1 > 10 ? new Date().getFullYear() + 1 : new Date().getFullYear()
    this.dialogLoading = true

    this.initMeData().then(async res => {
      if (res) {
        await this.getIgnoreMenuPermissData({
          perid: this.MeData[0].perid,
          page_id: '26'
        })
        this.getMenuPermissData().then(res => {
          if (res) {
            // if (this.MenuPermiss.length > 0 && this.MenuPermiss.filter(item => item.page_id == '26' && item.page_status == '1').length > 0) {
            this.maintain = false
            this.getPermissionData({
              perid: this.MeData[0].perid
            }).then(res => {
              if (res) {
                this.PermissionAdmin.map((Per_i, idx) => {
                  if (Per_i.permission_id == '19000') {
                    this.getMenuPermissData().then(res => {
                      if (res) {
                        if (this.MenuPermiss.length > 0 && this.MenuPermiss.filter(item => item.page_id == '26' && item.page_status == '1').length > 0 || this.IgnoreMenuPermiss.filter(item => item.perid == this.MeData[0].perid).length > 0) {
                          this.maintain = false
                          // this.TypeProject == null ? this.dialogProcess = true : this.ChooseProcess(this.TypeProject)
                          this.ChooseProcess('1')
                          this.dialogChooseProcess = true
                          this.dialogLoading = false
                          this.init()
                        } else {
                          this.pageMessage = this.MenuPermiss.filter(item => item.page_id == '26' && item.page_message != null).length > 0 ? this.MenuPermiss.filter(item => item.page_id == '26')[0].page_message : 'ปิดปรับปรุงระบบชั่วคราว'
                          this.maintain = true
                        }
                      }
                    }).then(err => {
                      if (err) {
                        this.pageMessage = this.MenuPermiss.filter(item => item.page_id == '26' && item.page_message != null).length > 0 ? this.MenuPermiss.filter(item => item.page_id == '26')[0].page_message : 'ปิดปรับปรุงระบบชั่วคราว'
                        this.maintain = true
                      }
                    })
                    this.CheckAdmin = true
                    this.dialogLoading = false
                  } else {
                    if (!this.CheckAdmin && idx == this.PermissionAdmin.length - 1) {
                      this.dialogLoading = false
                      this.$router.push('/')
                    }
                  }
                })
              } else {
                // this.$router.push('/')
                this.getPermissionUserData({
                  perid: this.MeData[0].perid
                }).then(res => {
                  if (res) {
                    this.Permission.map((Per_i, idx) => {
                      if (Per_i.permission_id == '19000') {
                        this.getMenuPermissData().then(res => {
                          if (res) {
                            if (this.MenuPermiss.length > 0 && this.MenuPermiss.filter(item => item.page_id == '26' && item.page_status == '1').length > 0 || this.IgnoreMenuPermiss.filter(item => item.perid == this.MeData[0].perid).length > 0) {
                              this.maintain = false
                              // this.TypeProject == null ? this.dialogProcess = true : this.ChooseProcess(this.TypeProject)
                              this.ChooseProcess('1')
                              this.dialogChooseProcess = true
                              this.dialogLoading = false
                              this.init()
                            } else {
                              this.pageMessage = this.MenuPermiss.filter(item => item.page_id == '26' && item.page_message != null).length > 0 ? this.MenuPermiss.filter(item => item.page_id == '26')[0].page_message : 'ปิดปรับปรุงระบบชั่วคราว'
                              this.maintain = true
                            }
                          }
                        }).then(err => {
                          if (err) {
                            this.pageMessage = this.MenuPermiss.filter(item => item.page_id == '26' && item.page_message != null).length > 0 ? this.MenuPermiss.filter(item => item.page_id == '26')[0].page_message : 'ปิดปรับปรุงระบบชั่วคราว'
                            this.maintain = true
                          }
                        })
                        this.CheckSubAdmin = true
                        this.dialogLoading = false
                      } else {
                        if (!this.CheckAdmin && idx == this.Permission.length - 1) {
                          this.dialogLoading = false
                          this.$router.push('/')
                        }
                      }
                    })
                  } else {
                    this.CheckSubAdmin = false
                    this.CheckAdmin = false
                    this.dialogLoading = false
                    this.$router.push('/')
                  }
                })
              }
            })
            // } else {
            //   this.maintain = true
            // }
          }
        }).then(err => {
          if (err) {
            this.maintain = true
          }
        })
      }
    }).catch(reason => {
      console.log('Reject = ' + reason)
    })
  }
};
</script>

<style scoped>
.chart-container {
  position: relative;
  height: 550px; /* เพิ่มความสูงจาก 450px เป็น 550px */
  width: 100%;
  padding: 20px; /* เพิ่ม padding */
  border-radius: 12px;
  background: linear-gradient(135deg, #fafbfc 0%, #f8f9fa 100%);
  box-shadow: inset 0 1px 3px rgba(0,0,0,0.1);
}

.chart-container-medium {
  position: relative;
  height: 400px; /* เพิ่มความสูงจาก 350px เป็น 400px */
  width: 100%;
  padding: 16px; /* เพิ่ม padding */
  border-radius: 10px;
  background: linear-gradient(135deg, #fafbfc 0%, #f8f9fa 100%);
}

.chart-container-small {
  position: relative;
  height: 280px; /* เพิ่มความสูงจาก 220px เป็น 280px */
  width: 100%;
  padding: 12px; /* เพิ่ม padding */
  border-radius: 8px;
  background: linear-gradient(135deg, #fafbfc 0%, #f8f9fa 100%);
}

.chart-container-mini {
  position: relative;
  height: 200px; /* เพิ่มความสูงจาก 150px เป็น 200px */
  width: 100%;
  padding: 8px; /* เพิ่ม padding */
  border-radius: 6px;
  background: linear-gradient(135deg, #fafbfc 0%, #f8f9fa 100%);
}

.v-chip-group {
  flex-wrap: wrap;
  gap: 8px;
}

.v-chip {
  margin: 4px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 20px !important;
  font-weight: 500;
}

.v-chip:hover {
  transform: translateY(-3px) scale(1.05);
  box-shadow: 0 6px 20px rgba(0,0,0,0.15);
}

.v-chip.v-chip--active {
  transform: scale(1.1);
  box-shadow: 0 4px 15px rgba(0,0,0,0.2);
}

.v-card {
  border-radius: 16px !important;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
}

.v-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 12px 40px rgba(0,0,0,0.15) !important;
}

.v-card-title {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid rgba(0,0,0,0.08);
  position: relative;
}

.v-card-title::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 60px;
  height: 3px;
  background: linear-gradient(90deg, #007bff, #28a745);
  border-radius: 2px;
}

.v-btn {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-weight: 600;
  letter-spacing: 0.5px;
}

.v-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0,0,0,0.2);
}

.v-dialog .v-card {
  box-shadow: 0 20px 60px rgba(0,0,0,0.3);
}

.v-subheader {
  color: rgba(0,0,0,0.7) !important;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  margin-bottom: 8px;
}

/* Animation for chart containers */
.chart-container, .chart-container-small, .chart-container-mini {
  opacity: 0;
  animation: fadeInUp 0.6s ease-out forwards;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive design improvements */
@media (max-width: 960px) {
  .chart-container {
    height: 450px; /* ลดความสูงสำหรับ tablet */
    padding: 16px;
  }
  
  .chart-container-small {
    height: 240px; /* ลดความสูงสำหรับ tablet */
    padding: 10px;
  }
  
  .chart-container-mini {
    height: 160px; /* ลดความสูงสำหรับ tablet */
    padding: 6px;
  }
  
  .v-card-title {
    font-size: 1.1rem !important;
    padding: 16px !important;
  }
}

@media (max-width: 600px) {
  .chart-container {
    height: 350px; /* ลดความสูงสำหรับมือถือ */
    padding: 12px;
  }
  
  .chart-container-small {
    height: 200px; /* ลดความสูงสำหรับมือถือ */
    padding: 8px;
  }
  
  .chart-container-mini {
    height: 140px; /* ลดความสูงสำหรับมือถือ */
    padding: 4px;
  }
  
  .v-chip {
    font-size: 11px !important;
    height: 24px !important;
  }
  
  .v-card-title {
    font-size: 1rem !important;
    padding: 12px !important;
  }
}

/* Custom scrollbar for dialog */
.v-dialog .v-card-text::-webkit-scrollbar {
  width: 8px;
}

.v-dialog .v-card-text::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.v-dialog .v-card-text::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.v-dialog .v-card-text::-webkit-scrollbar-thumb:hover {
  background: #a1a1a1;
}

/* Fiscal Year Select Styling */
.fiscal-year-select .v-input__control {
  border-radius: 12px !important;
}

.fiscal-year-select .v-select__selection {
  font-weight: 600 !important;
  color: #1976d2 !important;
}

.fiscal-year-select .v-input__slot {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%) !important;
  border: 2px solid #e0e0e0 !important;
  transition: all 0.3s ease !important;
}

.fiscal-year-select .v-input__slot:hover {
  border-color: #1976d2 !important;
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.15) !important;
}

.fiscal-year-select.v-input--is-focused .v-input__slot {
  border-color: #1976d2 !important;
  box-shadow: 0 4px 16px rgba(25, 118, 210, 0.25) !important;
}

/* Loading Dialog Styling */
.loading-dots {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.dots-container {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
}

.dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #1976d2;
  animation: pulse 1.4s ease-in-out infinite both;
}

.dot:nth-child(1) {
  animation-delay: -0.32s;
}

.dot:nth-child(2) {
  animation-delay: -0.16s;
}

.dot:nth-child(3) {
  animation-delay: 0s;
}

@keyframes pulse {
  0%, 80%, 100% {
    transform: scale(0.8);
    opacity: 0.5;
  }
  40% {
    transform: scale(1.2);
    opacity: 1;
  }
}

/* Loading icon spin animation */
.mdi-spin {
  animation: spin 2s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* Additional responsive styles for category charts */
@media (max-width: 600px) {
  .chart-container {
    height: 350px !important; /* สอดคล้องกับค่าด้านบน */
  }
  
  .chart-container-small {
    height: 200px !important; /* สอดคล้องกับค่าด้านบน */
  }
  
  .chart-container-mini {
    height: 140px !important; /* สอดคล้องกับค่าด้านบน */
  }
}

/* Summary Cards Styling */
.summary-card {
  position: relative;
  border-radius: 16px !important;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
}

.summary-card:hover {
  transform: translateY(-4px) !important;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
}

.summary-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.8), transparent);
  z-index: 1;
}

/* Category Summary Container */
.category-summary-container {
  padding: 0 8px;
}

/* Responsive adjustments for summary cards */
@media (max-width: 959px) {
  .summary-card {
    margin-bottom: 16px;
  }
}

@media (max-width: 599px) {
  .category-summary-container {
    padding: 0 4px;
  }
  
  .summary-card {
    margin-bottom: 12px;
    padding: 12px !important;
  }
  
  .summary-card .v-avatar {
    width: 40px !important;
    height: 40px !important;
  }
  
  .summary-card .text-h4 {
    font-size: 1.8rem !important;
  }
  
  .summary-card .text-h5 {
    font-size: 1.5rem !important;
  }
}

/* Progress bar enhancements */
.v-progress-linear {
  border-radius: 3px !important;
}

.v-progress-linear__buffer {
  border-radius: 3px !important;
}

.v-progress-linear__determinate {
  border-radius: 3px !important;
}

/* Animation for numbers and progress bars */
.animated-number {
  animation: fadeInUp 0.8s ease-out;
}

.animated-progress .v-progress-linear__determinate {
  animation: progressGrow 1.5s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes progressGrow {
  from {
    width: 0 !important;
  }
  to {
    width: var(--progress-width) !important;
  }
}

/* Summary card hover effects */
.summary-card .v-avatar {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.summary-card:hover .v-avatar {
  transform: scale(1.1);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
}

/* Card entrance animation */
.category-summary-container .v-col {
  animation: slideInUp 0.6s ease-out;
}

.category-summary-container .v-col:nth-child(1) { animation-delay: 0.1s; }
.category-summary-container .v-col:nth-child(2) { animation-delay: 0.2s; }
.category-summary-container .v-col:nth-child(3) { animation-delay: 0.3s; }
.category-summary-container .v-col:nth-child(4) { animation-delay: 0.4s; }

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Chip hover effects for chart type selectors */
.v-chip:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.2);
}

/* Card hover effects */
.v-card:hover {
  transform: translateY(-4px);
  transition: all 0.3s ease;
}

/* Category Summary Cards */
.category-summary-container {
  margin-top: 2rem;
}

.category-summary-card {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 12px !important;
  background: linear-gradient(135deg, #fafbfc 0%, #f8f9fa 100%);
  border: 1px solid rgba(0,0,0,0.08);
}

.category-summary-card:hover {
  transform: translateY(-3px) scale(1.02);
  box-shadow: 0 8px 25px rgba(0,0,0,0.15) !important;
  border-color: transparent;
}

.category-summary-card .v-avatar {
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  transition: all 0.3s ease;
}

.category-summary-card:hover .v-avatar {
  transform: scale(1.1);
  box-shadow: 0 6px 16px rgba(0,0,0,0.25);
}

.category-summary-card .text-h6 {
  transition: all 0.3s ease;
}

.category-summary-card:hover .text-h6 {
  transform: scale(1.05);
}

/* Responsive category summary */
@media (max-width: 960px) {
  .category-summary-card {
    margin-bottom: 16px;
  }
}

@media (max-width: 600px) {
  .category-summary-card .v-avatar {
    width: 32px !important;
    height: 32px !important;
  }
  
  .category-summary-card .v-icon {
    font-size: 16px !important;
  }
  
  .category-summary-card .text-h6 {
    font-size: 1rem !important;
  }
  
  .category-summary-card .text-caption {
    font-size: 0.7rem !important;
    font-family: 'Sarabun', sans-serif;
  }
}
</style>
